/**
 * Módulo 18 do v6 — MANUTENÇÃO PREDITIVA, na tela (0105): os tipos que o
 * servidor devolve, os rótulos e os retornos das gravações. A mesa e o
 * celular usam os mesmos.
 *
 * Nada aqui calcula previsão: o número vem do servidor, das funções puras de
 * `backend/src/preditiva/`. A tela só escreve — e, abaixo da base mínima,
 * escreve a FRASE do que falta, e nunca um número.
 */
import { formatarDecimal, formatarDia, formatarInteiro } from '@/lib/formato';

export type TomDoSelo = 'sucesso' | 'atencao' | 'erro' | 'informacao' | 'neutro';
export interface RotuloComTom {
  readonly rotulo: string;
  readonly tom: TomDoSelo;
}

/* =================================================================== tipos */

export const CATEGORIAS = ['desgaste', 'operacao', 'falta_de_manutencao', 'defeito_de_peca', 'condicao_de_terreno', 'indeterminada'] as const;
export type Categoria = (typeof CATEGORIAS)[number];

export type SituacaoDaFalha = 'a_classificar' | 'classificada' | 'sem_causa' | 'em_diagnostico';
export type FiltroDaFalha = 'todas' | SituacaoDaFalha;

export interface Maturidade {
  readonly suficiente: boolean;
  readonly falta: readonly string[];
}

export interface BaseMinima {
  readonly falhasMinimasRecorrencia: number;
  readonly maquinasMinimasRecorrencia: number;
  readonly exposicaoMinimaH: number;
  readonly intervalosMinimosPrevisao: number;
  readonly mesesMinimosHistorico: number;
  readonly coberturaMinimaSinalPct: number;
  readonly previsoesMinimasAdocao: number;
  readonly metaPrecisaoPct: number;
  readonly definidaPelaEmpresa: boolean;
}

export interface Premissas {
  readonly inclui: readonly string[];
  readonly naoInclui: readonly string[];
  readonly completo: boolean;
}

export interface Resumo {
  readonly corretivas: number;
  readonly classificadas: number;
  readonly aClassificar: number;
  readonly semCausa: number;
  readonly emDiagnostico: number;
  readonly acertoDaIa: { readonly sugestoes: number; readonly acertos: number; readonly pct: number | null };
  readonly modelosEmPiloto: number;
  readonly modelosAdotados: number;
  readonly previsoesAbertas: number;
  readonly propostasAbertas: number;
  readonly base: BaseMinima;
}

export interface FalhaNaLista {
  readonly ordemId: string;
  readonly numero: number;
  readonly equipamentoId: string;
  readonly equipamento: string;
  readonly etapa: string;
  readonly sintoma: string;
  readonly abertaEm: string;
  readonly falhaId: string | null;
  readonly componente: string | null;
  readonly componenteCodigo: string | null;
  readonly modo: string | null;
  readonly categoria: Categoria | null;
  readonly confianca: 'confirmada' | 'provavel' | null;
  readonly leituraNaFalha: number | null;
  readonly situacao: SituacaoDaFalha;
}

export interface RespostaDasFalhas {
  readonly falhas: readonly FalhaNaLista[];
  readonly total: number;
  readonly pagina: number;
  readonly porPagina: number;
}

export interface ItemDoCatalogo {
  readonly id: string;
  readonly codigo: string;
  readonly nome: string;
  readonly sistema?: string | null;
}

export interface Classificacao {
  readonly id: string;
  readonly componenteId: string;
  readonly componenteCodigo: string;
  readonly componente: string;
  readonly modoId: string | null;
  readonly modoCodigo: string | null;
  readonly modo: string | null;
  readonly categoria: Categoria;
  readonly confianca: 'confirmada' | 'provavel';
  readonly leituraNaFalha: number | null;
  readonly origemDaLeitura: OrigemDaLeitura | null;
  readonly observacao: string | null;
  readonly sugestaoId: string | null;
  readonly classificadaPor: string | null;
  readonly classificadaEm: string;
}

export type OrigemDaLeitura = 'anterior_a_falha' | 'liberacao' | 'informada';

export interface Sugestao {
  readonly id: string;
  readonly sabe: boolean;
  readonly componenteId: string | null;
  readonly componenteCodigo: string | null;
  readonly componente: string | null;
  readonly modoId: string | null;
  readonly modoCodigo: string | null;
  readonly modo: string | null;
  readonly categoria: Categoria | null;
  readonly falta: string | null;
  readonly modelo: string | null;
  readonly pedidaEm: string;
  readonly explicacao: string | null;
}

export interface DetalheDaFalha {
  readonly ordem: {
    readonly id: string;
    readonly numero: number;
    readonly etapa: string;
    readonly equipamentoId: string;
    readonly equipamento: string;
    readonly descricao: string;
    readonly modelo: string | null;
    readonly tipoMedidor: 'horimetro' | 'odometro';
    readonly sintoma: string;
    readonly diagnostico: string | null;
    readonly causaRaiz: string | null;
    readonly solucao: string | null;
    readonly abertaEm: string;
    readonly paradaDesde: string | null;
    readonly paradaAte: string | null;
    readonly situacao: SituacaoDaFalha;
  };
  readonly classificacao: Classificacao | null;
  readonly anuladas: number;
  readonly sugestao: Sugestao | null;
  readonly leituraSugerida: { readonly valor: number; readonly origem: OrigemDaLeitura; readonly lidaEm: string | null } | null;
  readonly catalogo: { readonly componentes: readonly ItemDoCatalogo[]; readonly modos: readonly ItemDoCatalogo[] };
}

export interface RespostaDaSugestao {
  readonly id: string;
  readonly sabe: boolean;
  readonly componente: { readonly id: string; readonly codigo: string; readonly nome: string } | null;
  readonly modo: { readonly id: string; readonly codigo: string; readonly nome: string } | null;
  readonly categoria: Categoria | null;
  readonly explicacao: string | null;
  readonly citacoes: readonly { readonly titulo: string; readonly texto: string }[];
  readonly falta: string | null;
  readonly modelo: string | null;
}

export interface ResultadoDaClassificacao {
  readonly id: string;
  readonly numero: number;
  readonly equipamento: string;
  readonly componente: string;
  readonly componenteCodigo: string;
  readonly modo: string | null;
  readonly categoria: Categoria;
  readonly confianca: 'confirmada' | 'provavel';
  readonly anulada?: boolean;
}

export interface ComponenteDoCatalogo {
  readonly id: string;
  readonly codigo: string;
  readonly nome: string;
  readonly sistemaId: string | null;
  readonly sistema: string | null;
  readonly modeloId: string | null;
  readonly modelo: string | null;
  readonly observacao: string | null;
  readonly ativo: boolean;
  readonly falhas: number;
}

export interface ModoDoCatalogo {
  readonly id: string;
  readonly codigo: string;
  readonly nome: string;
  readonly observacao: string | null;
  readonly ativo: boolean;
  readonly falhas: number;
}

export type Sinal = 'temperatura' | 'combustivel_nivel';

export interface LimiarDoCatalogo {
  readonly id: string;
  readonly modeloId: string;
  readonly modelo: string;
  readonly componenteId: string | null;
  readonly componente: string | null;
  readonly tipo: Sinal;
  readonly sentido: 'acima' | 'abaixo';
  readonly limite: number;
  readonly documentoId: string;
  readonly documento: string;
  readonly pagina: number;
  readonly trecho: string | null;
  readonly ativo: boolean;
}

export interface RespostaDoCatalogo {
  readonly componentes: readonly ComponenteDoCatalogo[];
  readonly modos: readonly ModoDoCatalogo[];
  readonly limiares: readonly LimiarDoCatalogo[];
  readonly modelos: readonly { readonly id: string; readonly nome: string }[];
  readonly documentos: readonly { readonly id: string; readonly titulo: string; readonly revisao: string | null; readonly paginas: number | null }[];
}

export interface LinhaDaPlanilha {
  readonly numeroLinha: number;
  readonly tipo: 'componente' | 'modo' | null;
  readonly codigo: string | null;
  readonly nome: string | null;
  readonly sistema: string | null;
  readonly modelo: string | null;
  readonly erros: readonly string[];
  readonly situacao: 'entra' | 'ja_cadastrado' | 'erro';
}

export interface ConferenciaDaPlanilha {
  readonly linhas: readonly LinhaDaPlanilha[];
  readonly entram: number;
  readonly jaCadastrados: number;
  readonly comErro: number;
}

export interface Indicador {
  readonly valor: number | null;
  readonly base: number;
  readonly premissas: Premissas;
}

export interface FatorAssociado {
  readonly chave: string;
  readonly rotulo: string;
  readonly falhas: number;
  readonly pctFalhas: number;
  readonly pctExposicao: number | null;
}

export interface GrupoDeRecorrencia {
  readonly chave: string;
  readonly modeloId: string;
  readonly modelo: string;
  readonly componenteId: string;
  readonly componente: string;
  readonly componenteCodigo: string;
  readonly modoId: string | null;
  readonly modo: string | null;
  readonly falhas: number;
  readonly maquinas: number;
  readonly ordens: readonly number[];
  readonly ordemIds: readonly string[];
  readonly recorrencia: Maturidade;
  readonly exposicaoH: number | null;
  readonly taxa: Maturidade;
  readonly taxaPorMilH: number | null;
  readonly mtbf: Indicador;
  readonly mttr: Indicador;
  readonly porCategoria: readonly FatorAssociado[] | null;
  readonly porObra: readonly FatorAssociado[] | null;
  readonly rotuloDaAssociacao: string;
}

export interface RespostaDaRecorrencia {
  readonly de: string;
  readonly ate: string;
  readonly base: BaseMinima;
  readonly falhas: number;
  readonly grupos: readonly GrupoDeRecorrencia[];
  readonly recorrentes: number;
  readonly premissas: Premissas;
}

export interface Investigacao {
  readonly id: string;
  readonly titulo: string;
  readonly situacao: 'aberta' | 'concluida' | 'descartada';
  readonly hipotese: string;
  readonly conclusao: string | null;
  readonly abertaEm: string;
  readonly abertaPor: string | null;
  readonly encerradaEm: string | null;
  readonly encerradaPor: string | null;
  readonly modeloId: string | null;
  readonly modelo: string | null;
  readonly componenteId: string | null;
  readonly componente: string | null;
  readonly modoId: string | null;
  readonly modo: string | null;
  readonly evidencias: number;
  readonly propostas: number;
}

export interface DetalheDaInvestigacao {
  readonly investigacao: { readonly id: string; readonly titulo: string; readonly situacao: string };
  readonly evidencias: readonly {
    readonly id: string;
    readonly ordemId: string;
    readonly numero: number;
    readonly equipamento: string;
    readonly falhouEm: string;
    readonly componente: string | null;
    readonly categoria: Categoria | null;
    readonly observacao: string | null;
  }[];
  readonly propostas: readonly { readonly id: string; readonly tipo: string; readonly situacao: string; readonly propostaEm: string }[];
}

export type TipoDeModelo = 'taxa_por_horas' | 'limiar_de_sinal';
export type SituacaoDoModelo = 'rascunho' | 'em_piloto' | 'adotado' | 'retirado';

export interface MedidaFora {
  readonly alertas: number;
  readonly confirmadas: number;
  readonly falsosAlertas: number;
  readonly emAberto: number;
  readonly naoPrevistas: number;
  readonly precisaoPct: number | null;
  readonly coberturaPct: number | null;
  readonly antecedenciaMedia: number | null;
}

export interface ResultadoDoBacktest {
  readonly tipo: TipoDeModelo;
  readonly corte: string;
  readonly calculadoEm: string;
  readonly treino: { readonly intervalos: number; readonly limite: number | null; readonly idadeDeAlerta: number | null; readonly de: string | null; readonly ate: string | null } | null;
  readonly teste: MedidaFora | null;
  readonly maturidade: Maturidade;
  readonly abstencao: string | null;
  readonly premissas: Premissas;
}

export interface Placar {
  readonly emitidas: number;
  readonly abertas: number;
  readonly confirmadas: number;
  readonly falsosAlertas: number;
  readonly expiradas: number;
  readonly comDesfecho: number;
  readonly precisaoPct: number | null;
  readonly antecedenciaMedia: number | null;
  readonly falhasNaoPrevistas: number;
}

export interface ModeloPreditivo {
  readonly id: string;
  readonly nome: string;
  readonly tipo: TipoDeModelo;
  readonly modeloId: string;
  readonly modelo: string;
  readonly componenteId: string;
  readonly componente: string;
  readonly modoId: string | null;
  readonly modo: string | null;
  readonly parametros: Record<string, number | string>;
  readonly limiar: { readonly tipo: string; readonly sentido: string; readonly limite: number; readonly documento: string; readonly pagina: number } | null;
  readonly versao: number;
  readonly situacao: SituacaoDoModelo;
  readonly corteDoTreino: string | null;
  readonly backtest: ResultadoDoBacktest | null;
  readonly pilotoDesde: string | null;
  readonly adotadoEm: string | null;
  readonly retiradoEm: string | null;
  readonly motivoRetirada: string | null;
  readonly criadoEm: string;
  readonly criadoPor: string | null;
  readonly ultimaRodada: string | null;
  readonly placar: Placar | null;
  readonly adocao: Maturidade | null;
}

export interface RespostaDosModelos {
  readonly base: BaseMinima;
  readonly sugeridos: {
    readonly taxa: { readonly percentil: number; readonly horizonteH: number };
    readonly limiar: { readonly janelaH: number; readonly leiturasMinimas: number; readonly horizonteDias: number };
  };
  readonly modelos: readonly ModeloPreditivo[];
}

export interface Abstencao {
  readonly equipamentoId: string | null;
  readonly codigo: string | null;
  readonly motivo: string;
}

export interface ResultadoDaRodada {
  readonly rodadaId: string;
  readonly nome: string;
  readonly avaliadas: number;
  readonly emitidas: number;
  readonly confirmadas: number;
  readonly expiradas: number;
  readonly abstencoes: readonly Abstencao[];
}

export interface Rodada {
  readonly id: string;
  readonly versao: number;
  readonly executadaEm: string;
  readonly executadaPor: string | null;
  readonly avaliadas: number;
  readonly emitidas: number;
  readonly confirmadas: number;
  readonly expiradas: number;
  readonly abstencoes: readonly Abstencao[];
}

export type SituacaoDaPrevisao = 'aberta' | 'confirmada' | 'falso_alerta' | 'expirada';

export interface Previsao {
  readonly id: string;
  readonly modeloId: string;
  readonly modelo: string;
  readonly situacaoDoModelo: SituacaoDoModelo;
  readonly versao: number;
  readonly equipamentoId: string;
  readonly equipamento: string;
  readonly componenteId: string;
  readonly componente: string;
  readonly emitidaEm: string;
  readonly horizonte: number;
  readonly unidadeDoHorizonte: 'h' | 'dias';
  readonly horimetroNaEmissao: number | null;
  readonly venceEm: string | null;
  readonly valor: number;
  readonly limite: number;
  readonly evidencia: { readonly fatos: readonly string[]; readonly unidade: string; readonly premissas: Premissas };
  readonly situacao: SituacaoDaPrevisao;
  readonly desfechoOrdemId: string | null;
  readonly desfechoNumero: number | null;
  readonly desfechoEm: string | null;
  readonly desfechoPor: string | null;
  readonly desfechoNota: string | null;
  readonly antecedencia: number | null;
}

export interface RespostaDasPrevisoes {
  readonly previsoes: readonly Previsao[];
  readonly total: number;
  readonly pagina: number;
  readonly porPagina: number;
}

export type SituacaoDaProposta = 'proposta' | 'aceita' | 'recusada';

export interface PropostaTecnica {
  readonly id: string;
  readonly tipo: 'inspecao' | 'ajuste_de_plano';
  readonly situacao: SituacaoDaProposta;
  readonly equipamentoId: string | null;
  readonly equipamento: string | null;
  readonly planoId: string | null;
  readonly plano: string | null;
  readonly tarefaId: string | null;
  readonly tarefa: string | null;
  readonly componente: string | null;
  readonly investigacao: string | null;
  readonly gatilho: 'horas' | 'km' | 'dias' | null;
  readonly intervaloAtual: number | null;
  readonly intervaloProposto: number | null;
  readonly documento: string | null;
  readonly pagina: number | null;
  readonly contrariaFabricante: boolean;
  readonly justificativa: string;
  readonly propostaPorId: string | null;
  readonly propostaPor: string | null;
  readonly propostaEm: string;
  readonly decididaPor: string | null;
  readonly decididaEm: string | null;
  readonly notaDaDecisao: string | null;
  readonly ordemId: string | null;
  readonly ordemNumero: number | null;
  readonly revisaoPlanoId: string | null;
}

export interface RespostaDasPropostas {
  readonly propostas: readonly PropostaTecnica[];
  readonly eu: string;
}

export interface OpcoesDaProposta {
  readonly planos: readonly {
    readonly id: string;
    readonly nome: string;
    readonly documentoId: string | null;
    readonly documento: string | null;
    readonly tarefas: readonly { readonly id: string; readonly descricao: string; readonly gatilho: 'horas' | 'km' | 'dias'; readonly intervalo: number; readonly pagina: number | null }[];
  }[];
  readonly documentos: readonly { readonly id: string; readonly titulo: string; readonly revisao: string | null; readonly paginas: number | null }[];
  readonly componentes: readonly ItemDoCatalogo[];
  readonly investigacoes: readonly { readonly id: string; readonly titulo: string }[];
}

/* ================================================================ rótulos */

/** A situação da corretiva no histórico: nunca um dos seis status da máquina. */
export const SITUACAO_DA_FALHA: Readonly<Record<SituacaoDaFalha, RotuloComTom & { readonly oQueFazer: string }>> = {
  classificada: { rotulo: 'Classificada', tom: 'sucesso', oQueFazer: 'Entra no histórico padronizado e na recorrência.' },
  a_classificar: { rotulo: 'A classificar', tom: 'atencao', oQueFazer: 'Tem diagnóstico e causa: diga o componente, o modo e a categoria.' },
  sem_causa: {
    rotulo: 'Sem causa registrada',
    tom: 'neutro',
    oQueFazer: 'Não classificável: sem causa registrada. A O.S. encerrada não muda, e fica fora da base.',
  },
  em_diagnostico: { rotulo: 'Em diagnóstico', tom: 'informacao', oQueFazer: 'Classifique depois do diagnóstico e da causa raiz escritos na O.S.' },
};

export const FILTROS_DA_FALHA: readonly FiltroDaFalha[] = ['todas', 'a_classificar', 'classificada', 'sem_causa', 'em_diagnostico'];
export const ROTULO_DO_FILTRO_DA_FALHA: Readonly<Record<FiltroDaFalha, string>> = {
  todas: 'Todas',
  a_classificar: 'A classificar',
  classificada: 'Classificadas',
  sem_causa: 'Sem causa',
  em_diagnostico: 'Em diagnóstico',
};

export const ROTULO_DA_CATEGORIA: Readonly<Record<Categoria, string>> = {
  desgaste: 'Desgaste',
  operacao: 'Operação',
  falta_de_manutencao: 'Falta de manutenção',
  defeito_de_peca: 'Defeito de peça',
  condicao_de_terreno: 'Condição de terreno',
  indeterminada: 'Indeterminada',
};

export const ROTULO_DA_CONFIANCA = { confirmada: 'Confirmada', provavel: 'Provável' } as const;

export const ROTULO_DA_ORIGEM_DA_LEITURA: Readonly<Record<OrigemDaLeitura, string>> = {
  anterior_a_falha: 'a última leitura antes da falha',
  liberacao: 'a leitura da liberação',
  informada: 'informada por quem classificou',
};

export const ROTULO_DO_TIPO_DE_MODELO: Readonly<Record<TipoDeModelo, string>> = {
  taxa_por_horas: 'Horas desde a última falha',
  limiar_de_sinal: 'Sinal acima do limite do fabricante',
};

export const SITUACAO_DO_MODELO: Readonly<Record<SituacaoDoModelo, RotuloComTom>> = {
  rascunho: { rotulo: 'Rascunho', tom: 'neutro' },
  em_piloto: { rotulo: 'Em piloto', tom: 'informacao' },
  adotado: { rotulo: 'Adotado', tom: 'sucesso' },
  retirado: { rotulo: 'Retirado', tom: 'neutro' },
};

export const SITUACAO_DA_PREVISAO: Readonly<Record<SituacaoDaPrevisao, RotuloComTom>> = {
  aberta: { rotulo: 'Aberta', tom: 'atencao' },
  confirmada: { rotulo: 'Confirmada', tom: 'sucesso' },
  falso_alerta: { rotulo: 'Falso alerta', tom: 'erro' },
  expirada: { rotulo: 'Expirada', tom: 'neutro' },
};

export const SITUACAO_DA_PROPOSTA: Readonly<Record<SituacaoDaProposta, RotuloComTom>> = {
  proposta: { rotulo: 'Para decidir', tom: 'atencao' },
  aceita: { rotulo: 'Aceita', tom: 'sucesso' },
  recusada: { rotulo: 'Recusada', tom: 'neutro' },
};

export const ROTULO_DO_TIPO_DE_PROPOSTA = { inspecao: 'Inspeção', ajuste_de_plano: 'Ajuste de plano' } as const;
export const ROTULO_DO_SINAL: Readonly<Record<Sinal, string>> = { temperatura: 'Temperatura', combustivel_nivel: 'Nível de combustível' };
export const UNIDADE_DO_SINAL: Readonly<Record<Sinal, string>> = { temperatura: '°C', combustivel_nivel: '%' };
const UNIDADE_DO_GATILHO = { horas: 'h', km: 'km', dias: 'dias' } as const;

/** O rótulo de toda tabela de fatores: o mesmo do servidor (`preditiva/recorrencia.ts`). */
export const ROTULO_DA_ASSOCIACAO = 'Associação, não causa';

/* ================================================================ escrita */

export const numeroDaOrdem = (n: number): string => `O.S. ${String(n).padStart(6, '0')}`;
export const pct = (v: number | null): string => (v === null ? '—' : `${formatarDecimal(v, 1).replace(/,0$/, '')}%`);
export const horas = (v: number | null): string => (v === null ? '—' : `${formatarDecimal(v, 1).replace(/,0$/, '')} h`);
export const quando = (iso: string | null, vazio = '—'): string => (iso ? formatarDia(iso) : vazio);

export function intervaloEscrito(v: number | null, gatilho: PropostaTecnica['gatilho']): string {
  if (v === null || gatilho === null) return '—';
  return `${formatarDecimal(v, 1).replace(/,0$/, '')} ${UNIDADE_DO_GATILHO[gatilho]}`;
}

/** "Amostra insuficiente: faltam 2 falhas…; …." — a mesma frase do servidor. */
export function fraseDaFalta(m: Maturidade, oQue: string): string {
  return m.suficiente ? '' : `Amostra insuficiente para ${oQue}: ${m.falta.join('; ')}.`;
}

/** O horizonte da previsão: "250 h de uso" ou "14 dias, até 21/10/2026". */
export function horizonteEscrito(p: Pick<Previsao, 'horizonte' | 'unidadeDoHorizonte' | 'venceEm'>): string {
  return p.unidadeDoHorizonte === 'h'
    ? `${formatarDecimal(p.horizonte, 1).replace(/,0$/, '')} h de uso`
    : `${formatarInteiro(p.horizonte)} dias, até ${quando(p.venceEm)}`;
}

/** O valor contra o limite, na unidade da evidência: "530 h · limite 620 h". */
export function valorContraLimite(p: Pick<Previsao, 'valor' | 'limite' | 'evidencia'>): string {
  const u = p.evidencia.unidade;
  const n = (x: number) => formatarDecimal(x, 1).replace(/,0$/, '');
  return `${n(p.valor)} ${u} · limite ${n(p.limite)} ${u}`;
}

/** O placar em uma linha, com a falha não prevista AO LADO do acerto. */
export function placarEscrito(p: Placar | null): string {
  if (!p || p.emitidas === 0) return p && p.falhasNaoPrevistas > 0 ? `Nenhuma previsão · ${formatarInteiro(p.falhasNaoPrevistas)} falhas não previstas` : 'Nenhuma previsão ainda';
  return `${formatarInteiro(p.confirmadas)} de ${formatarInteiro(p.comDesfecho)} confirmadas · ${formatarInteiro(p.falhasNaoPrevistas)} ${p.falhasNaoPrevistas === 1 ? 'falha não prevista' : 'falhas não previstas'}`;
}

/** A medida do backtest numa linha, ou a abstenção. */
export function backtestEscrito(b: ResultadoDoBacktest | null): string {
  if (!b) return 'Sem backtest: rode com uma data de corte.';
  if (b.abstencao) return b.abstencao;
  const t = b.teste;
  if (!t) return 'O backtest não teve o que medir depois do corte.';
  return `Corte em ${quando(b.corte)}: precisão ${pct(t.precisaoPct)}, ${formatarInteiro(t.confirmadas)} confirmadas, ${formatarInteiro(t.falsosAlertas)} falsos alertas, ${formatarInteiro(t.naoPrevistas)} falhas não previstas.`;
}

/** Os parâmetros do modelo como a tela os escreve. */
export function parametrosEscritos(m: Pick<ModeloPreditivo, 'tipo' | 'parametros' | 'limiar'>): string {
  const p = m.parametros;
  if (m.tipo === 'taxa_por_horas') {
    return `Percentil ${formatarInteiro(Math.round(Number(p['percentil']) * 100))} dos intervalos · horizonte de ${formatarInteiro(Number(p['horizonteH']))} h`;
  }
  const l = m.limiar;
  return `${l ? `${l.tipo === 'temperatura' ? 'Temperatura' : 'Nível'} ${l.sentido} de ${formatarDecimal(l.limite, 1).replace(/,0$/, '')} ${l.tipo === 'temperatura' ? '°C' : '%'} (${l.documento}, p. ${formatarInteiro(l.pagina)})` : 'Limite do fabricante'}`
    + ` · janela de ${formatarInteiro(Number(p['janelaH']))} h · ${formatarInteiro(Number(p['leiturasMinimas']))} leituras · ${formatarInteiro(Number(p['horizonteDias']))} dias`;
}

/* =============================================================== retornos */

type SemTitulo = Omit<import('@/lib/retorno').RetornoDeclarado, 'titulo'>;

export const retornoDaClassificacao = (r: ResultadoDaClassificacao): SemTitulo => ({
  alvo: { codigo: numeroDaOrdem(r.numero), nome: r.equipamento },
  fatos: [`${r.componenteCodigo} · ${r.componente}`, r.modo, `${ROTULO_DA_CATEGORIA[r.categoria]} · ${ROTULO_DA_CONFIANCA[r.confianca].toLowerCase()}`],
  efeito: 'Entra no histórico padronizado e na recorrência; a O.S. não muda',
});

export const retornoDaAnulacao = (r: ResultadoDaClassificacao): SemTitulo => ({
  alvo: { codigo: numeroDaOrdem(r.numero), nome: r.equipamento },
  fatos: [`${r.componenteCodigo} · ${r.componente}`],
  efeito: 'Sai do histórico e da recorrência; a trilha guarda o motivo',
});

export const retornoDaSugestao = (r: RespostaDaSugestao): SemTitulo =>
  r.sabe && r.componente
    ? { fatos: [`${r.componente.codigo} · ${r.componente.nome}`, r.modo ? `${r.modo.codigo} · ${r.modo.nome}` : null, r.categoria ? ROTULO_DA_CATEGORIA[r.categoria] : null], efeito: 'É sugestão: confira e classifique' }
    : { tom: 'ressalva', fatos: [r.falta ? `Falta: ${r.falta}` : null], efeito: 'A IA se absteve: classifique pela leitura da O.S.' };

export const retornoDoCatalogo = (r: { readonly codigo: string; readonly nome: string }, tipo: 'componente' | 'modo'): SemTitulo => ({
  alvo: { codigo: r.codigo, nome: r.nome },
  efeito: tipo === 'componente' ? 'Passa a ser oferecido na classificação' : 'Passa a ser oferecido na classificação, com o componente',
});

export const retornoDaImportacao = (r: { readonly componentes: number; readonly modos: number; readonly deFora: number }): SemTitulo => ({
  tom: r.deFora > 0 ? 'ressalva' : 'sucesso',
  fatos: [`${formatarInteiro(r.componentes)} ${r.componentes === 1 ? 'componente' : 'componentes'}`, `${formatarInteiro(r.modos)} ${r.modos === 1 ? 'modo' : 'modos'}`, r.deFora > 0 ? `${formatarInteiro(r.deFora)} de fora` : null],
  efeito: r.deFora > 0 ? 'As linhas de fora estão na conferência, com o que corrigir' : 'O catálogo da planilha entrou inteiro',
});

export const retornoDoLimiar = (r: { readonly tipo: Sinal; readonly sentido: 'acima' | 'abaixo'; readonly limite: number; readonly pagina: number }): SemTitulo => ({
  fatos: [`${ROTULO_DO_SINAL[r.tipo]} ${r.sentido} de ${formatarDecimal(r.limite, 1).replace(/,0$/, '')} ${UNIDADE_DO_SINAL[r.tipo]}`, `Página ${formatarInteiro(r.pagina)}`],
  efeito: 'Pode ser o limite de um modelo de sinal',
});

export const retornoDaInvestigacao = (r: { readonly titulo: string; readonly evidencias: number }): SemTitulo => ({
  alvo: { nome: r.titulo },
  fatos: [`${formatarInteiro(r.evidencias)} ${r.evidencias === 1 ? 'O.S. como evidência' : 'O.S. como evidência'}`],
  efeito: 'Aberta: a hipótese ainda não é causa',
});

export const retornoDaConclusao = (r: { readonly titulo: string; readonly situacao: string }): SemTitulo => ({
  alvo: { nome: r.titulo },
  efeito: r.situacao === 'concluida' ? 'Concluída: a conclusão fica como foi escrita' : 'Descartada: a conclusão fica como foi escrita',
});

export const retornoDoModelo = (r: { readonly nome: string; readonly versao: number }): SemTitulo => ({
  alvo: { nome: r.nome },
  fatos: [`Versão ${formatarInteiro(r.versao)}`],
  efeito: 'Em rascunho: rode o backtest antes do piloto',
});

export const retornoDoBacktest = (r: ResultadoDoBacktest): SemTitulo => ({
  tom: r.abstencao ? 'ressalva' : 'sucesso',
  fatos: r.teste
    ? [`Precisão ${pct(r.teste.precisaoPct)}`, `${formatarInteiro(r.teste.confirmadas)} confirmadas`, `${formatarInteiro(r.teste.naoPrevistas)} não previstas`]
    : [r.abstencao],
  efeito: r.abstencao ? 'Sem medida: o modelo não vai a piloto assim' : 'Medido fora da amostra: pode ir a piloto',
});

export const retornoDaRodada = (r: ResultadoDaRodada): SemTitulo => ({
  tom: r.abstencoes.length > 0 ? 'ressalva' : 'sucesso',
  alvo: { nome: r.nome },
  fatos: [
    `${formatarInteiro(r.emitidas)} ${r.emitidas === 1 ? 'previsão emitida' : 'previsões emitidas'}`,
    `${formatarInteiro(r.abstencoes.length)} ${r.abstencoes.length === 1 ? 'abstenção' : 'abstenções'}`,
    r.confirmadas + r.expiradas > 0 ? `${formatarInteiro(r.confirmadas)} confirmadas, ${formatarInteiro(r.expiradas)} expiradas` : null,
  ],
  efeito: 'Cada abstenção diz o que falta; a previsão não é diagnóstico',
});

export const retornoDaDecisao = (r: { readonly situacao: string; readonly tipo: string; readonly ordem: { readonly rotulo: string } | null; readonly revisaoPlanoId: string | null }): SemTitulo => ({
  fatos: [r.situacao === 'aceita' ? 'Aceita' : 'Recusada'],
  efeito: r.ordem
    ? `A O.S. de inspeção ${r.ordem.rotulo} foi aberta`
    : r.revisaoPlanoId
      ? 'A revisão do plano está em rascunho: o intervalo muda quando alguém a publicar com a nota'
      : 'Nada muda no plano nem na oficina',
});

export const retornoDaProposta = (r: { readonly tipo: string; readonly contrariaFabricante: boolean; readonly equipamento: string | null }): SemTitulo => ({
  tom: r.contrariaFabricante ? 'ressalva' : 'sucesso',
  ...(r.equipamento ? { alvo: { codigo: r.equipamento } } : {}),
  fatos: [ROTULO_DO_TIPO_DE_PROPOSTA[r.tipo as 'inspecao'], r.contrariaFabricante ? 'Contraria o fabricante' : null],
  efeito: 'Vai para a decisão técnica: quem propõe não decide',
});

export const retornoDoDesfecho = (r: { readonly situacao: string; readonly equipamento: string; readonly componente: string; readonly numero: number | null; readonly antecedencia: number | null; readonly unidade: 'h' | 'dias' }): SemTitulo => ({
  alvo: { codigo: r.equipamento, nome: r.componente },
  fatos: [
    r.situacao === 'confirmada' ? 'Confirmada' : 'Falso alerta',
    r.numero !== null ? numeroDaOrdem(r.numero) : null,
    r.antecedencia !== null ? `Antecedência de ${formatarDecimal(r.antecedencia, 1).replace(/,0$/, '')} ${r.unidade === 'h' ? 'h' : 'dias'}` : null,
  ],
  efeito: 'Conta no placar do modelo',
});

export const retornoDaBase = (b: BaseMinima): SemTitulo => ({
  fatos: [`${formatarInteiro(b.previsoesMinimasAdocao)} previsões para adotar`, `meta de precisão ${pct(b.metaPrecisaoPct)}`],
  efeito: 'Vale para a recorrência, a previsão e a adoção desta empresa',
});

/** Hoje, no formato do campo de data (aaaa-mm-dd), menos `dias`. */
export function diaDoCampo(diasAtras = 0): string {
  const d = new Date(Date.now() - diasAtras * 86_400_000);
  return `${String(d.getFullYear())}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/* ============================================================ o endereço */

export interface BuscaDasFalhas {
  readonly situacao?: SituacaoDaFalha;
  readonly pagina?: number;
}
export interface BuscaDaRecorrencia {
  readonly de?: string;
  readonly ate?: string;
}
export interface BuscaDasPrevisoes {
  readonly situacao?: SituacaoDaPrevisao;
  readonly pagina?: number;
}
export interface BuscaDasPropostas {
  readonly situacao?: SituacaoDaProposta;
}

const paginaDa = (x: unknown): { pagina?: number } => {
  const n = Number(x);
  return Number.isInteger(n) && n > 1 && n <= 10_000 ? { pagina: n } : {};
};
const diaDa = (x: unknown): string | undefined => (typeof x === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(x) ? x : undefined);

export function validarBuscaDasFalhas(e: Record<string, unknown>): BuscaDasFalhas {
  const s = e['situacao'];
  return {
    ...(typeof s === 'string' && (FILTROS_DA_FALHA as readonly string[]).includes(s) && s !== 'todas' ? { situacao: s as SituacaoDaFalha } : {}),
    ...paginaDa(e['pagina']),
  };
}

export function validarBuscaDaRecorrencia(e: Record<string, unknown>): BuscaDaRecorrencia {
  const de = diaDa(e['de']);
  const ate = diaDa(e['ate']);
  return { ...(de ? { de } : {}), ...(ate ? { ate } : {}) };
}

export function validarBuscaDasPrevisoes(e: Record<string, unknown>): BuscaDasPrevisoes {
  const s = e['situacao'];
  return {
    ...(typeof s === 'string' && s in SITUACAO_DA_PREVISAO ? { situacao: s as SituacaoDaPrevisao } : {}),
    ...paginaDa(e['pagina']),
  };
}

export function validarBuscaDasPropostas(e: Record<string, unknown>): BuscaDasPropostas {
  const s = e['situacao'];
  return typeof s === 'string' && s in SITUACAO_DA_PROPOSTA ? { situacao: s as SituacaoDaProposta } : {};
}
